import React, { useEffect, useState, useRef, Suspense } from "react";
import { Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Text, Float } from "@react-three/drei";
import * as THREE from "three";
import AOS from "aos";
import "aos/dist/aos.css";
import "../Styles/Home.css";
import ChatBot from "./ChatBot";
import {
  AiOutlineArrowRight,
  AiOutlineRocket,
  AiOutlineCloudServer,
  AiOutlineRobot,
  AiOutlineGlobal,
  AiOutlineEye,
  AiOutlineThunderbolt,
  AiOutlineCode,
  AiOutlineDatabase,
  AiOutlineSecurityScan,
  AiOutlineBulb,
} from "react-icons/ai";
import {
  FaFacebook,
  FaWhatsapp,
  FaYoutube,
  FaLinkedin,
  FaInstagram,
  FaGithub,
  FaGlobeAmericas,
  FaSyncAlt,
  FaHandPointer,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { TbWorld } from "react-icons/tb";
// 3D Globe Component
// 3D Globe Component - Simplified version
// 3D Globe Component - Fixed Version
function InteractiveGlobe({ onCountryClick }) {
  const globeRef = useRef();
  const [hovered, setHovered] = useState(null);
  const [rotationSpeed, setRotationSpeed] = useState(0.001);

  useFrame((state) => {
    if (globeRef.current) {
      globeRef.current.rotation.y += rotationSpeed;
    }
  });

  const countries = [
    {
      name: "Kenya",
      lat: 0.0236,
      lng: 37.9062,
      color: "#10B981",
      projects: 15,
    },
    {
      name: "USA",
      lat: 37.0902,
      lng: -95.7129,
      color: "#3B82F6",
      projects: 45,
    },
    { name: "UK", lat: 55.3781, lng: -3.436, color: "#8B5CF6", projects: 28 },
    {
      name: "Germany",
      lat: 51.1657,
      lng: 10.4515,
      color: "#EF4444",
      projects: 22,
    },
    {
      name: "Japan",
      lat: 36.2048,
      lng: 138.2529,
      color: "#F59E0B",
      projects: 19,
    },
    {
      name: "Australia",
      lat: -25.2744,
      lng: 133.7751,
      color: "#EC4899",
      projects: 12,
    },
    {
      name: "Nigeria",
      lat: 9.082,
      lng: 8.6753,
      color: "#06B6D4",
      projects: 18,
    },
    {
      name: "South Africa",
      lat: -30.5595,
      lng: 22.9375,
      color: "#84CC16",
      projects: 14,
    },
  ];

  // Convert lat/lng to 3D coordinates
  const latLngToVector3 = (lat, lng, radius = 1.02) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);

    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.sin(theta);

    return [x, y, z];
  };

  return (
    <group>
      {/* Ambient and directional lights */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -10]} color="#6366f1" intensity={0.5} />

      {/* Stars background */}
      <Stars
        radius={300}
        depth={60}
        count={5000}
        factor={4}
        saturation={0}
        fade
      />

      {/* Fixed Main Globe - Ensure all material properties are defined */}
      <mesh ref={globeRef} castShadow receiveShadow>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial
          color="#0f172a"
          metalness={0.3}
          roughness={0.7}
          emissive="#0f172a"
          emissiveIntensity={0.2}
          transparent={true}
          opacity={0.9}
          // Add required properties to avoid undefined errors
          side={THREE.FrontSide}
          wireframe={false}
          flatShading={false}
          fog={false}
          // Ensure all uniforms are initialized
          defines={{
            STANDARD: "",
            PHYSICAL: "",
            USE_UV: "",
            USE_MAP: "",
          }}
        />
      </mesh>

      {/* Country markers with fixed materials */}
      {countries.map((country, idx) => {
        const position = latLngToVector3(country.lat, country.lng);

        return (
          <group key={idx}>
            <Float speed={2} rotationIntensity={1} floatIntensity={2}>
              <mesh
                position={position}
                onPointerOver={() => setHovered(country.name)}
                onPointerOut={() => setHovered(null)}
                onClick={() => onCountryClick(country)}
              >
                <sphereGeometry args={[0.03, 16, 16]} />
                <meshBasicMaterial
                  color={country.color}
                  transparent={true}
                  opacity={hovered === country.name ? 1 : 0.8}
                  // Add required properties
                  side={THREE.FrontSide}
                  wireframe={false}
                  fog={false}
                />
              </mesh>
            </Float>

            {/* Simplified hover effect */}
            {hovered === country.name && (
              <mesh position={position}>
                <ringGeometry args={[0.05, 0.07, 32]} />
                <meshBasicMaterial
                  color="white"
                  transparent={true}
                  opacity={0.5}
                  side={THREE.DoubleSide}
                  wireframe={false}
                  fog={false}
                />
              </mesh>
            )}
          </group>
        );
      })}

      {/* Orbit controls */}
      <OrbitControls
        enableZoom={true}
        enablePan={true}
        enableRotate={true}
        zoomSpeed={0.6}
        panSpeed={0.5}
        rotateSpeed={0.8}
        minDistance={1.5}
        maxDistance={4}
        autoRotate={false}
      />
    </group>
  );
}
// Stats Counter Component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    if (start === end) return;

    const incrementTime = Math.abs(Math.floor(duration / end));
    let timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{count}</span>;
};
function Home() {
  useEffect(() => {
    AOS.init({
      duration: 1500,
      once: true,
    });
  }, []);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [activeTech, setActiveTech] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const handleCountryClick = (country) => {
    setSelectedCountry(country);
    // Show modal with country details
    console.log(
      `Selected country: ${country.name} with ${country.projects} projects`
    );
  };
  // Futuristic tech features
  const techFeatures = [
    {
      icon: <AiOutlineRobot className="tech-icon" />,
      title: "AI & Machine Learning",
      description: "Advanced neural networks for intelligent automation",
      gradient: "from-purple-500 to-pink-500",
      delay: "100",
    },
    {
      icon: <AiOutlineCloudServer className="tech-icon" />,
      title: "Cloud Native",
      description: "Scalable microservices on Kubernetes & AWS",
      gradient: "from-blue-500 to-cyan-500",
      delay: "200",
    },
    {
      icon: <AiOutlineDatabase className="tech-icon" />,
      title: "Big Data Analytics",
      description: "Real-time processing with Apache Spark & Kafka",
      gradient: "from-green-500 to-emerald-500",
      delay: "300",
    },
    {
      icon: <AiOutlineSecurityScan className="tech-icon" />,
      title: "Cyber Security",
      description: "End-to-end encryption & threat detection",
      gradient: "from-red-500 to-orange-500",
      delay: "400",
    },
  ];
  // Global stats
  const globalStats = [
    { value: "200+", label: "Projects Worldwide", icon: <AiOutlineRocket /> },
    { value: "50+", label: "Countries Served", icon: <FaGlobeAmericas /> },
    { value: "98%", label: "Client Satisfaction", icon: <AiOutlineBulb /> },
    { value: "24/7", label: "Global Support", icon: <FaSyncAlt /> },
  ];
  // Global clients
  const globalClients = [
    {
      name: "Afrikonsults",
      region: "Africa",
      logo: "🌍",
      description: "Agricultural tech solutions across 15+ African countries",
      color: "bg-green-500/10",
    },
    {
      name: "Trendy Gadgets",
      region: "Global",
      logo: "🛒",
      description: "E-commerce platform serving 30+ countries worldwide",
      color: "bg-blue-500/10",
    },
    {
      name: "Gym Classic",
      region: "North America",
      logo: "💪",
      description: "Health & fitness app with 1M+ users globally",
      color: "bg-purple-500/10",
    },
    {
      name: "Nairobi Cleaning",
      region: "East Africa",
      logo: "✨",
      description: "Service platform operating in 5+ countries",
      color: "bg-cyan-500/10",
    },
  ];
  return (
    <div className="home-container">
      {/* Hero Section - Futuristic Design */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-glow"></div>
          <div className="hero-grid"></div>
          <div className="hero-particles">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="hero-content">
          <div data-aos="fade-up" data-aos-delay="200" className="hero-text">
            <div className="hero-badge">
              <AiOutlineRobot />
              <span>AI-Powered Innovation</span>
            </div>

            <h1 className="hero-title">
              <span className="hero-title-line">
                <span className="gradient-text">Fusion</span>
                <span className="hero-title-accent">-Xe</span>
              </span>
              <span className="hero-subtitle">
                Redefining Digital Excellence
              </span>
            </h1>

            <p className="hero-description">
              We architect tomorrow's technology today. Through artificial
              intelligence, quantum-inspired algorithms, and immersive digital
              experiences, we transform businesses into future-ready
              enterprises.
            </p>

            <div className="hero-buttons">
              <Link to="/contact" className="btn-primary btn-glow">
                <span>Begin Transformation</span>
                <AiOutlineArrowRight className="btn-icon" />
              </Link>
              <Link to="/services" className="btn-secondary">
                Explore Capabilities
              </Link>
            </div>

            {/* Tech stack indicators */}
            <div className="tech-indicators">
              <div className="tech-indicator" data-tech="AI/ML">
                <div className="tech-dot"></div>
                <span>AI/ML</span>
              </div>
              <div className="tech-indicator" data-tech="Cloud">
                <div className="tech-dot"></div>
                <span>Cloud</span>
              </div>
              <div className="tech-indicator" data-tech="Blockchain">
                <div className="tech-dot"></div>
                <span>Blockchain</span>
              </div>
              <div className="tech-indicator" data-tech="IoT">
                <div className="tech-dot"></div>
                <span>IoT</span>
              </div>
            </div>
          </div>
        </div>
        {/* AI Assistant */}
        <div className="ai-assistant">
          <div className="ai-pulse"></div>
          <AiOutlineRobot className="ai-icon" />
          <span className="ai-text">Neural Assistant Online</span>
        </div>
      </section>
      {/* Tech Features */}
      <section className="tech-section">
        <div className="container">
          <div data-aos="fade-up" className="section-header">
            <div className="section-badge">
              <AiOutlineCode />
              <span>Core Technologies</span>
            </div>
            <h2>
              <span className="gradient-text">Quantum-Leap</span> Innovation
            </h2>
            <p>
              Leveraging cutting-edge technologies to deliver unparalleled
              solutions
            </p>
          </div>

          <div className="tech-grid">
            {techFeatures.map((tech, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={tech.delay}
                className={`tech-card ${activeTech === index ? "active" : ""}`}
                onMouseEnter={() => setActiveTech(index)}
              >
                <div className="tech-card-inner">
                  <div
                    className={`tech-icon-container bg-gradient-to-r ${tech.gradient}`}
                  >
                    {tech.icon}
                  </div>
                  <h3>{tech.title}</h3>
                  <p>{tech.description}</p>
                  <div className="tech-hover-line"></div>
                  <div className="tech-glow"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Global Presence Section with 3D Globe */}
      <section className="global-section">
        <div className="container">
          <div data-aos="fade-up" className="section-header">
            <div className="section-badge">
              <TbWorld />
              <span>Global Footprint</span>
            </div>
            <h2>
              <span className="gradient-text">Worldwide</span> Impact
            </h2>
            <p>
              Transforming businesses across the globe with our innovative
              solutions
            </p>
          </div>

          <div className="global-content">
            {/* 3D Globe Container */}
            <div data-aos="fade-right" className="globe-container">
              <div className="globe-wrapper">
                <Canvas shadows camera={{ position: [0, 0, 3], fov: 50 }}>
                  <Suspense fallback={null}>
                    <InteractiveGlobe onCountryClick={handleCountryClick} />
                  </Suspense>
                </Canvas>

                {/* Globe Controls */}
                <div className="globe-controls">
                  <div className="control-hint">
                    <FaHandPointer />
                    <span>Click on any country</span>
                  </div>
                  <div className="control-hint">
                    <FaSyncAlt />
                    <span>Drag to rotate globe</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Global Stats */}
            <div data-aos="fade-left" className="global-stats">
              <div className="stats-header">
                <h3>Our Global Impact</h3>
                <p>Numbers that define our worldwide presence</p>
              </div>

              <div className="stats-grid">
                {globalStats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-content">
                      <h4>
                        <AnimatedCounter value={parseInt(stat.value)} />
                        {!stat.value.includes("/") && "+"}
                      </h4>
                      <p>{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected Country Info */}
              {selectedCountry && (
                <div className="country-info">
                  <div className="country-info-header">
                    <h4>Selected: {selectedCountry.name}</h4>
                    <button
                      onClick={() => setSelectedCountry(null)}
                      className="close-btn"
                    >
                      ×
                    </button>
                  </div>
                  <div className="country-details">
                    <div className="detail-item">
                      <span className="detail-label">Projects Completed:</span>
                      <span className="detail-value">
                        {selectedCountry.projects}+
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Region:</span>
                      <span className="detail-value">Global Operations</span>
                    </div>
                    <Link to="/contact" className="detail-link">
                      Request Local Consultation <AiOutlineArrowRight />
                    </Link>
                  </div>
                </div>
              )}

              <div className="global-cta">
                <p>Ready to expand your business globally?</p>
                <Link to="/contact" className="btn-primary">
                  Connect with Our Global Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Global Clients */}
      <section className="clients-section">
        <div className="container">
          <div data-aos="fade-up" className="section-header">
            <h2>
              Trusted by <span className="gradient-text">Global</span> Leaders
            </h2>
            <p>
              Join industry leaders who trust us with their digital
              transformation
            </p>
          </div>

          <div className="clients-grid">
            {globalClients.map((client, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="client-card"
              >
                <div className={`client-logo ${client.color}`}>
                  {client.logo}
                </div>
                <div className="client-content">
                  <div className="client-header">
                    <h3>{client.name}</h3>
                    <span className="client-region">{client.region}</span>
                  </div>
                  <p>{client.description}</p>
                  <div className="client-tech">
                    <span className="tech-tag">AI</span>
                    <span className="tech-tag">Cloud</span>
                    <span className="tech-tag">Mobile</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div data-aos="fade-up" className="cta-content">
            <div className="cta-glow"></div>
            <h2>Begin Your Digital Evolution</h2>
            <p>
              Join the future with AI-driven solutions tailored for global
              impact
            </p>
            <div className="cta-buttons">
              <Link
                to="mailto:info@fusion-xe.com"
                className="btn-primary btn-glow"
              >
                <span>info@fusion-xe.com</span>
              </Link>
              <Link to="tel:+254741210060" className="btn-secondary">
                <span>+254 741 210060</span>
              </Link>
            </div>
            <div className="cta-info">
              <div className="info-item">
                <AiOutlineSecurityScan />
                <span>Enterprise-Grade Security</span>
              </div>
              <div className="info-item">
                <AiOutlineGlobal />
                <span>24/7 Global Support</span>
              </div>
              <div className="info-item">
                <AiOutlineRobot />
                <span>AI-Powered Solutions</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Social Links */}
      <div className="social-links">
        {[
          { icon: <FaFacebook />, href: "https://www.facebook.com/kingcolloh0" },
          { icon: <FaXTwitter />, href: "https://x.com/mwenda_collin" },
          {
            icon: <FaLinkedin />,
            href: "https://www.linkedin.com/in/collin-mwenda-software-engineer/",
          },
          { icon: <FaGithub />, href: "https://github.com/CollinM254" },
          { icon: <FaWhatsapp />, href: "https://wa.me/254741210060" },
        ].map((social, index) => (
          <a
            key={index}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            data-aos="fade-up"
            data-aos-delay={index * 100}
          >
            {social.icon}
          </a>
        ))}
      </div>
      <ChatBot />
    </div>
  );
}
export default Home;
